package br.com.texeratx.boardgametimeapp.ui.screens.active_match

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.active_match.ActiveMatchController
import br.com.texeratx.boardgametimeapp.core.active_match.MatchDraft
import br.com.texeratx.boardgametimeapp.core.active_match.PlayerScoreDraft
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.data.model.match.CreateMatchRequest
import br.com.texeratx.boardgametimeapp.data.model.match.CreateMatchScoreRequest
import br.com.texeratx.boardgametimeapp.data.repository.MatchRepository
import br.com.texeratx.boardgametimeapp.ui.screens.home.HomeScreen
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDateTime
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.koin.compose.getKoin
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors

class MatchScoreScreen : Screen {

    @Composable
    override fun Content() {
        val S = LocalStrings.current
        val navigator = LocalNavigator.currentOrThrow
        val koin = getKoin()

        val controller = koin.get<ActiveMatchController>()
        val matchRepository = koin.get<MatchRepository>()
        val draft by controller.lastFinishedDraft.collectAsState()

        val scope = rememberCoroutineScope()
        val snackbarHostState = remember { SnackbarHostState() }

        // Se entrou aqui sem draft, volta pra Home
        if (draft == null) {
            navigator.replaceAll(HomeScreen())
            return
        }

        MatchScoreUI(
            draft = draft!!,
            snackbarHostState = snackbarHostState,
            onBack = { navigator.pop() },
            onSave = { updatedDraft, observationText, scores, setSavingFalse ->

                // ✅ 1) Validação local: parsear scores
                val parsedScores = scores.map { item ->
                    val points = parsePoints(item.pointsText)
                        ?: return@MatchScoreUI Result.failure(
                            Exception(S.invalidScoreFor(item.playerName))
                        )

                    CreateMatchScoreRequest(
                        playerId = item.playerId,
                        points = points
                    )
                }

                // ✅ 2) Montar body do POST
                val body = CreateMatchRequest(
                    clanGameId = updatedDraft.gameId,
                    date = updatedDraft.dateIso,
                    duration = updatedDraft.durationMinutes,
                    observation = observationText.trim().ifBlank { null },
                    scores = parsedScores,
                    clanExpansionIds = updatedDraft.expansionIds
                )

                // ✅ 3) Chamar backend + Snackbar + navegar
                scope.launch {
                    val result = matchRepository.createMatch(body)

                    if (result.isSuccess) {
                        snackbarHostState.showSnackbar(S.snackbarMatchSaved)
                        // dá um tempinho pro usuário ver (opcional)
                        delay(500)

                        controller.clearLastFinishedDraft()
                        controller.clear()
                        navigator.replaceAll(HomeScreen())
                    } else {
                        snackbarHostState.showSnackbar(
                            result.exceptionOrNull()?.message ?: S.snackbarErrorSavingMatch
                        )
                        // libera botão novamente
                        setSavingFalse()
                    }
                }

                Result.success(Unit)
            }
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun MatchScoreUI(
    draft: MatchDraft,
    snackbarHostState: SnackbarHostState,
    onBack: () -> Unit,
    onSave: (
        updatedDraft: MatchDraft,
        observationText: String,
        scores: List<PlayerScoreDraft>,
        setSavingFalse: () -> Unit
    ) -> Result<Unit>
) {
    val editableScores = remember(draft) {
        mutableStateListOf<PlayerScoreDraft>().apply { addAll(draft.scores) }
    }

    // ✅ erros por jogador (key = playerId)
    val fieldErrors = remember { mutableStateOf<Map<Long, String>>(emptyMap()) }

    var observationText by remember(draft) { mutableStateOf(draft.observation.orEmpty()) }
    var isSaving by remember { mutableStateOf(false) }
    var generalError by remember { mutableStateOf<String?>(null) }

    val S = LocalStrings.current

    AppScreenScaffold(
        topBar = {
            TopAppBar(
                title = { Text(S.scoreTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                colors = AppTopBarColors()
            )
        },
        snackbarHost = { SnackbarHost(hostState = snackbarHostState) },
        bottomBar = {
            // ✅ botão fixo embaixo (melhor UX)
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .navigationBarsPadding()
                    .padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                generalError?.let {
                    Text(it, color = MaterialTheme.colorScheme.error)
                }

                Button(
                    onClick = {
                        generalError = null
                        fieldErrors.value = emptyMap()

                        // ✅ valida local por jogador (obrigatório + número)
                        val newErrors = buildMap<Long, String> {
                            editableScores.forEach { item ->
                                val t = item.pointsText.trim()
                                if (t.isBlank()) {
                                    put(item.playerId, S.required)
                                } else if (parsePoints(t) == null) {
                                    put(item.playerId, S.invalid)
                                }
                            }
                        }

                        if (newErrors.isNotEmpty()) {
                            fieldErrors.value = newErrors
                            return@Button
                        }

                        isSaving = true

                        val updatedDraft = draft.copy(
                            scores = editableScores.toList(),
                            observation = observationText.trim().ifBlank { null }
                        )

                        val validation = onSave(
                            updatedDraft,
                            observationText,
                            editableScores.toList(),
                            { isSaving = false }
                        )

                        if (validation.isFailure) {
                            isSaving = false
                            generalError = validation.exceptionOrNull()?.message ?: S.errorTitle
                        }
                    },
                    enabled = !isSaving,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    if (isSaving) {
                        androidx.compose.material3.CircularProgressIndicator(
                            strokeWidth = 2.dp,
                            modifier = Modifier.height(18.dp)
                        )
                    } else {
                        Text(S.saveMatch)
                    }
                }
            }
        }
    ) { padding ->

        Column(
            modifier = Modifier
                .padding(padding)
                .padding(horizontal = 16.dp)
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            Spacer(Modifier.height(12.dp))

            // ✅ Card do jogo (thumb + infos)
            MatchGameHeaderCard(
                gameName = draft.gameName,
                gameImageUrl = draft.gameImageUrl,
                dateIso = draft.dateIso,
                durationMinutes = draft.durationMinutes
            )

            Spacer(Modifier.height(8.dp))

            Text(
                S.sectionPlayerScores,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold
            )

            // ✅ Lista de jogadores em cards
            editableScores.forEachIndexed { index, score ->
                val avatarUrl = draft.playerImageUrls.getOrNull(index)
                val errorText = fieldErrors.value[score.playerId]

                PlayerScoreRow(
                    playerName = score.playerName,
                    avatarUrl = avatarUrl,
                    value = score.pointsText,
                    errorText = errorText,
                    enabled = !isSaving,
                    onValueChange = { text ->
                        val normalized = normalizePointsInput(text)
                        editableScores[index] = score.copy(pointsText = normalized)

                        // remove erro quando o usuário digitar algo válido
                        if (errorText != null) {
                            val parsed = parsePoints(normalized)
                            val cleared = normalized.trim().isNotBlank() && parsed != null
                            if (cleared) {
                                fieldErrors.value = fieldErrors.value - score.playerId
                            }
                        }
                    }
                )
            }

            Spacer(Modifier.height(6.dp))

            OutlinedTextField(
                value = observationText,
                onValueChange = { observationText = it },
                label = { Text(S.labelObservationOptional) },
                modifier = Modifier.fillMaxWidth(),
                minLines = 2,
                enabled = !isSaving
            )

            // espaço para não ficar colado no bottomBar
            Spacer(Modifier.height(90.dp))
        }
    }
}

@Composable
private fun MatchGameHeaderCard(
    gameName: String,
    gameImageUrl: String?,
    dateIso: String,
    durationMinutes: Int
) {
    androidx.compose.material3.ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        shape = androidx.compose.foundation.shape.RoundedCornerShape(18.dp)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {

            if (!gameImageUrl.isNullOrBlank()) {
                br.com.texeratx.boardgametimeapp.ui.components.GameThumb(
                    imageUrl = gameImageUrl,
                    contentDescription = gameName,
                    modifier = Modifier
                        .size(width = 64.dp, height = 84.dp)
                        .clip(androidx.compose.foundation.shape.RoundedCornerShape(14.dp))
                )
            } else {
                br.com.texeratx.boardgametimeapp.ui.screens.games.GameCover(
                    imageUrl = null,
                    title = gameName,
                    modifier = Modifier
                        .size(width = 64.dp, height = 84.dp)
                        .clip(androidx.compose.foundation.shape.RoundedCornerShape(14.dp))
                )
            }

            Spacer(Modifier.width(12.dp))

            val S = LocalStrings.current

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = gameName,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Spacer(Modifier.height(4.dp))
                Text(
                    text = S.matchDateDisplay(apiToUiDateTime(dateIso)),
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                Text(
                    text = S.matchDurationMinFormat(durationMinutes.toLong()),
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}

@Composable
private fun PlayerScoreRow(
    playerName: String,
    avatarUrl: String?,
    value: String,
    errorText: String?,
    enabled: Boolean,
    onValueChange: (String) -> Unit
) {
    val S = LocalStrings.current
    androidx.compose.material3.ElevatedCard(
        modifier = Modifier.fillMaxWidth(),
        shape = androidx.compose.foundation.shape.RoundedCornerShape(16.dp)
    ) {
        Row(
            modifier = Modifier
                .padding(12.dp)
                .fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {

            br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar(
                imageUrl = avatarUrl,
                contentDescription = playerName,
                modifier = Modifier.size(44.dp)
            )

            Spacer(Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = playerName,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold
                )
                if (!errorText.isNullOrBlank()) {
                    Spacer(Modifier.height(2.dp))
                    Text(
                        text = errorText,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.labelSmall
                    )
                }
            }

            Spacer(Modifier.width(12.dp))

            OutlinedTextField(
                value = value,
                onValueChange = onValueChange,
                label = { Text(S.labelPts) },
                isError = !errorText.isNullOrBlank(),
                singleLine = true,
                enabled = enabled,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                modifier = Modifier.width(130.dp)
            )
        }
    }
}

/**
 * Faz parse aceitando vírgula ou ponto.
 * Retorna null se vazio ou inválido.
 */
private fun parsePoints(text: String): Double? {
    val t = text.trim()
    if (t.isBlank()) return null
    return t.replace(',', '.').toDoubleOrNull()
}

private fun normalizePointsInput(text: String): String {
    val filtered = text.filter { it.isDigit() || it == ',' || it == '.' }

    // mantém só 1 separador decimal
    return buildString {
        var sepCount = 0
        for (ch in filtered) {
            if (ch == ',' || ch == '.') {
                if (sepCount == 0) {
                    append(ch)
                    sepCount++
                }
            } else {
                append(ch)
            }
        }
    }
}

